import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { ArrowLeft, Download, Eye, Calendar, User, Loader2, Store, FileEdit, MoreVertical, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { EditInSprintDialog } from '@/components/kb/EditInSprintDialog';
import { DocumentPreviewDialog } from '@/components/kb/DocumentPreviewDialog';

export const KnowledgeBaseViewer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const { data: kb, isLoading, error } = useQuery({
    queryKey: ['knowledge-base', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('knowledge_bases')
        .select(`
          *,
          profiles:creator_id(display_name, avatar_url),
          domains:domain_id(name, symbol),
          knowledge_base_domains(
            domains(id, name, symbol, description)
          ),
          attributions(
            user_id,
            ownership_percentage,
            contribution_type,
            profiles:user_id(display_name, avatar_url)
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      // Increment view count
      const { error: updateError } = await supabase
        .from('knowledge_bases')
        .update({ views_count: (data.views_count || 0) + 1 })
        .eq('id', id);
      
      if (updateError) console.error('Failed to increment view count:', updateError);

      return data;
    }
  });


  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('knowledge_bases')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge_bases'] });
      toast({
        title: "Knowledge Base Deleted",
        description: "The knowledge base has been removed.",
      });
      navigate(-1);
    },
    onError: () => {
      toast({
        title: "Failed to Delete Knowledge Base",
        description: "Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleEdit = () => {
    navigate(`/edit-knowledge-base/${id}`);
  };

  const handleMint = () => {
    toast({
      title: "Mint Knowledge Base",
      description: "Web3 minting capability will be available soon!",
    });
  };

  const handleDelete = () => {
    if (id) {
      deleteMutation.mutate(id);
      setShowDeleteDialog(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !kb) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-lg text-muted-foreground">Knowledge base not found</p>
        <Button onClick={() => navigate(-1)} variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <Button 
        onClick={() => navigate('/myspai-profile')} 
        variant="ghost" 
        className="mb-6"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back
      </Button>

      <div className="space-y-6">
        <h1 className="text-4xl font-bold mb-4">{kb.title}</h1>
        
        {/* Multiple Domain Badges */}
        {kb.knowledge_base_domains && kb.knowledge_base_domains.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {kb.knowledge_base_domains.map((kbDomain: any) => (
              <Badge key={kbDomain.domains.id} variant="secondary">
                {kbDomain.domains.symbol || kbDomain.domains.name}
              </Badge>
            ))}
          </div>
        )}
        
        {/* Fallback to old single domain if no multi-domain associations yet */}
        {(!kb.knowledge_base_domains || kb.knowledge_base_domains.length === 0) && kb.domains && (
          <Badge variant="secondary" className="mb-4">
            {kb.domains.symbol || kb.domains.name}
          </Badge>
        )}

        {kb.description && (
          <p className="text-lg text-muted-foreground mb-6">{kb.description}</p>
        )}

          {/* Action Buttons */}
          {kb.status === 'published' && kb.channel_name && (
            <div className="mb-6">
              <Button 
                variant="outline"
                size="lg"
                onClick={() => navigate(`/channel/${kb.channel_name}/solutions?highlight=${kb.id}`)}
              >
                <Store className="h-5 w-5 mr-2" />
                View on Marketplace
              </Button>
            </div>
          )}

        {/* Creator Info */}
        {kb.profiles && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  {kb.profiles.avatar_url && (
                    <img 
                      src={kb.profiles.avatar_url} 
                      alt={kb.profiles.display_name}
                      className="h-10 w-10 rounded-full"
                    />
                  )}
                  <div>
                    <p className="text-sm text-muted-foreground">Created by</p>
                    <p className="font-medium">{kb.profiles.display_name}</p>
                  </div>
                </div>
                
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" sideOffset={5}>
                    {kb.status === 'published' && kb.channel_name && (
                      <DropdownMenuItem onClick={() => navigate(`/channel/${kb.channel_name}/solutions?highlight=${kb.id}`)}>
                        View on Marketplace
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={handleEdit}>
                      Edit
                    </DropdownMenuItem>
                    {!kb.nft_cover_image_url && (
                      <DropdownMenuItem onClick={handleMint}>
                        Mint
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem 
                      className="text-red-600"
                      onClick={() => setShowDeleteDialog(true)}
                    >
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardContent>
          </Card>
        )}

        {/* File Section */}
        {kb.file_url && (
          <Card>
            <CardContent className="pt-6 space-y-4">
              <h2 className="text-xl font-semibold">Attached File</h2>
              
              {kb.nft_cover_image_url && (
                <img 
                  src={kb.nft_cover_image_url} 
                  alt={kb.title}
                  className="w-full h-64 object-cover rounded-lg"
                />
              )}

            <div className="flex items-center justify-between p-4 bg-accent rounded-lg text-white">
              <div>
                <p className="font-medium text-white">{kb.file_name}</p>
                <p className="text-sm text-white/80">
                  {kb.file_type} • {kb.file_size ? (kb.file_size / 1024).toFixed(2) : '0'} KB
                </p>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="secondary"
                  onClick={() => setEditDialogOpen(true)}
                >
                  <FileEdit className="h-4 w-4 mr-2" />
                  Edit in Sprint
                </Button>
                <Button 
                  className="bg-green-600 text-white hover:bg-green-700"
                  onClick={() => setPreviewDialogOpen(true)}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Preview
                </Button>
                <Button asChild>
                  <a href={kb.file_url} download target="_blank" rel="noopener noreferrer">
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </a>
                </Button>
              </div>
            </div>
            </CardContent>
          </Card>
        )}

        {/* Metadata */}
        <Card>
          <CardContent className="pt-6">
            <h2 className="text-xl font-semibold mb-4">Metadata</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Version</p>
                <p className="font-medium">{kb.version}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <Eye className="h-4 w-4" />
                  Views
                </p>
                <p className="font-medium">{kb.views_count || 0}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <Badge variant={kb.nft_cover_image_url ? 'default' : 'secondary'}>
                  {kb.nft_cover_image_url ? 'Published' : 'Draft'}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <Store className="h-4 w-4" />
                  Marketplace
                </p>
                {kb.nft_cover_image_url ? (
                  <Link 
                    to={`/marketplace/${kb.channel_name || 'general'}`}
                    className="font-medium text-primary hover:underline flex items-center gap-1"
                  >
                    Minted ✓
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                ) : (
                  <p className="font-medium text-muted-foreground">Not Minted</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Attributions */}
        {kb.attributions && kb.attributions.length > 0 && (
          <Card>
            <CardContent className="pt-6">
              <h2 className="text-xl font-semibold mb-4">Contributors</h2>
              <div className="space-y-3">
                {kb.attributions.map((attribution: any) => (
                <div key={attribution.user_id} className="flex items-center justify-between p-3 bg-accent rounded-lg text-white">
                  <div className="flex items-center gap-3">
                    {attribution.profiles?.avatar_url && (
                      <img 
                        src={attribution.profiles.avatar_url} 
                        alt={attribution.profiles.display_name}
                        className="h-8 w-8 rounded-full"
                      />
                    )}
                    <div>
                      <p className="font-medium text-white">{attribution.profiles?.display_name || 'Unknown'}</p>
                      <p className="text-sm text-white/80">{attribution.contribution_type}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-white border-white/30">
                    {attribution.ownership_percentage}% ownership
                  </Badge>
                </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Content */}
        {kb.content && (
          <Card>
            <CardContent className="pt-6">
              <h2 className="text-xl font-semibold mb-4">Content</h2>
              <div className="prose prose-sm max-w-none">
                <p className="whitespace-pre-wrap">{kb.content}</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Edit in Sprint Dialog */}
      <EditInSprintDialog
        kb={kb}
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
      />

      {/* Document Preview Dialog */}
      <DocumentPreviewDialog
        open={previewDialogOpen}
        onOpenChange={setPreviewDialogOpen}
        fileUrl={kb.file_url || ''}
        fileName={kb.file_name || ''}
        fileType={kb.file_type || ''}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete this knowledge base?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove "{kb.title}" from your list. The knowledge base will be archived in our system for record-keeping purposes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
